
export enum ModuleType {
  MESSENGER = 'messenger',
  CALENDAR = 'calendar',
  ABSENCES = 'absences',
  JOURNAL = 'journal',
  ADDRESSBOOK = 'addressbook',
  REQUESTS = 'requests',
  ADMIN = 'admin',
}

export interface JournalEntry {
  date: string;
  content: string;
  updatedAt: string;
  items?: JournalEntryItem[];
}

export interface JournalEntryItem {
  id: string;
  templateId?: string;
  text: string;
}

export interface JournalTemplate {
  id: string;
  title: string;
  unit: string;
  parentTemplateId?: string;
  sortOrder?: number;
  matchTerms: string[];
  createdAt: string;
  updatedAt: string;
}

export interface JournalPlanItem {
  id: string;
  templateId: string;
  quantity: number;
  month: string;
  createdAt: string;
  updatedAt: string;
}

export interface JournalReport {
  id: string;
  dateFrom: string;
  dateTo: string;
  status: string;
  createdAt: string;
  downloadUrl: string;
  fileName?: string;
}

export interface GenerateJournalReportPayload {
  dateFrom: string;
  dateTo: string;
}

export type AddressBookScope = 'global' | 'personal';

export type AddressBookPhone = {
  id: string;
  phoneRaw: string;
  phoneE164?: string | null;
  label?: string | null;
  isPrimary: boolean;
};

export type AddressBookEmail = {
  id: string;
  email: string;
  label?: string | null;
  isPrimary: boolean;
};

export type AddressBookContact = {
  id: string;
  scope: AddressBookScope;
  ownerId?: string | null;
  name: string;
  position?: string | null;
  organization?: string | null;
  note?: string | null;
  createdAt?: string;
  updatedAt?: string;
  phones: AddressBookPhone[];
  emails: AddressBookEmail[];
};

export enum UserStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
  MANAGER = 'manager',
}

export interface UserPreferences {
    theme?: 'light' | 'dark';
    themeColor?: string; // 'blue', 'green', 'violet', 'orange', 'rose'
    chatWallpaper?: string; // css background value
    fontScale?: number; // UI scale multiplier, e.g. 0.9..1.2
    calendarSystemColors?: Partial<Record<CalendarType, string>>;
    notifications: {
        push: boolean;
        sound: boolean;
        soundVolume?: number;
        vibration?: boolean;
        calendarCreate: boolean;
        reminders: boolean;
        reminderTime: number; // minutes
        calendarIds?: string[];
    };
}

export interface User {
  id: string;
  name: string;
  username: string;
  avatar?: string;
  status: UserStatus;
  statusMessage?: string; // Custom text status
  lastSeen?: Date; // Timestamp when user was last online
  role: string; // Changed from UserRole to string to support dynamic roles
  roleName?: string;
  department?: string;
  departmentName?: string;
  departmentCalendarId?: string;
  jobTitle?: string;
  email?: string;
  isFired?: boolean;
  birthDate?: Date;
  preferences?: UserPreferences;
}

export enum MessageStatus {
  SENDING = 'sending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
}

export interface PollOption {
  id: string;
  text: string;
  voterIds: string[];
}

export interface Poll {
  question: string;
  options: PollOption[];
  allowMultiple: boolean;
  isClosed: boolean;
}

export interface MessageEditHistoryEntry {
  text: string;
  timestamp: Date;
}

export interface Message {
  id: string;
  senderId: string;
  text: string;
  content?: any | null;
  timestamp: Date;
  editedAt?: Date; // When the message was last edited
  editHistory?: MessageEditHistoryEntry[];
  status: MessageStatus;
  attachments?: FileAttachment[];
  replyToId?: string;
  forwardedFrom?: string; // ID of the original sender if forwarded
  reactions?: Record<string, string[]>; // emoji -> userIds
  poll?: Poll;
  readBy?: string[]; // List of user IDs who read the message
  scheduledFor?: Date; // If set, message is scheduled
  isStarred?: boolean; // Starred/Favorite message
}

 export interface ScheduledMessage {
   id: string;
   senderId: string;
   text: string;
   attachments?: FileAttachment[];
   replyToId?: string | null;
   scheduledFor: Date;
 }

export interface FileAttachment {
  fileId?: string;
  name: string;
  size: number;
  type: string;
  url?: string;
}

export enum ChatType {
  PRIVATE = 'private',
  GROUP_SYSTEM = 'group_system',
  GROUP_USER = 'group_user',
}

export interface Chat {
  id: string;
  type: ChatType;
  name: string;
  participants: string[]; // User IDs
  messages: Message[];
  scheduledMessages?: ScheduledMessage[]; // Pending scheduled messages
  unreadCount: number;
  avatar?: string;
  adminIds?: string[]; // For group chats
  isPinned?: boolean;
  pinnedMessageId?: string;
  isMuted?: boolean;
}

export enum CalendarType {
  PERSONAL = 'personal',
  SYSTEM_GENERAL = 'system_general',
  SYSTEM_DEPT = 'system_dept',
  USER_CUSTOM = 'user_custom',
  BIRTHDAYS = 'birthdays',
}

export enum EventRecurrence {
  NONE = 'none',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export interface CustomCalendar {
  id: string;
  name: string;
  color: string;
  ownerId: string;
  sharedWithIds: string[];
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  location?: string;
  start: Date;
  end: Date;
  isAllDay: boolean;
  type: CalendarType;
  color: string;
  createdBy: string;
  files?: FileAttachment[];
  calendarId?: string; // If belongs to a custom calendar
  departmentId?: string; // For SYSTEM_DEPT visibility
  recurrence?: EventRecurrence;
  recurrenceId?: string;
  recurrenceParentId?: string;
}

export interface Department {
  id: string;
  name: string;
  headUserId?: string;
}

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error';
  message: string;
  userId?: string;
}

export interface BackupEntry {
  id: string;
  filename: string;
  size: string;
  date: Date;
}

export interface Notification {
    id: string;
    type: 'chat' | 'calendar' | 'system' | 'mention';
    title: string;
    text: string;
    date: Date;
    linkId?: string; // Chat ID or Event ID
    subId?: string;  // Message ID or Date ISO
    unread?: number;
    isRead: boolean;
}
